// =============================================================================
// features/sales/campaigns/wizard/components/ActivateConfirmDialog.tsx —
// Confirmação de impacto da ativação (etapa 6, DoD: "diz quantas pessoas
// entram na fila de quais vendedores. Confirmação genérica não é
// confirmação"). Mesmo padrão de diálogo modal de
// features/sales/campaigns/operate/components/ReturnMemberDialog.tsx (fora
// de files_allowed — não importado, só o mesmo tratamento visual/foco).
// =============================================================================

import * as React from 'react';

import { Button } from '../../../../../components/ui/Button';
import { cn } from '../../../../../lib/cn';
import { useFocusTrap } from '../../../components/useFocusTrap';

export interface ActivateConfirmDialogProps {
  campaignName: string;
  strategyLabel: string;
  membersTotal: number;
  membersBlocked: number;
  vendorPoolSize: number;
  onClose: () => void;
  onConfirm: () => Promise<{ ok: true } | { ok: false; message: string }>;
}

export function ActivateConfirmDialog({
  campaignName,
  strategyLabel,
  membersTotal,
  membersBlocked,
  vendorPoolSize,
  onClose,
  onConfirm,
}: ActivateConfirmDialogProps): React.JSX.Element {
  const panelRef = React.useRef<HTMLDivElement>(null);
  const [isPending, setIsPending] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<string | null>(null);

  const workable = membersTotal - membersBlocked;

  const handleEscape = React.useCallback(() => {
    if (!isPending) onClose();
  }, [isPending, onClose]);

  useFocusTrap({ containerRef: panelRef, active: true, onEscape: handleEscape });

  async function handleConfirm(): Promise<void> {
    setIsPending(true);
    setSubmitError(null);
    const result = await onConfirm();
    setIsPending(false);
    if (!result.ok) {
      setSubmitError(result.message);
      return;
    }
    onClose();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'color-mix(in srgb, var(--text) 60%, transparent)' }}
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleEscape();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="activate-campaign-title"
        className="w-full max-w-md rounded-md flex flex-col overflow-hidden"
        style={{
          background: 'var(--bg-elev-1)',
          boxShadow: 'var(--elev-5)',
          border: '1px solid var(--border)',
          animation: 'fade-up 200ms var(--ease-out) both',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="flex flex-col gap-1 px-6 py-5"
          style={{ borderBottom: '1px solid var(--border)' }}
        >
          <h2
            id="activate-campaign-title"
            className="font-display font-bold text-ink"
            style={{ fontSize: 'var(--text-xl)', letterSpacing: '-0.03em' }}
          >
            Ativar “{campaignName}”?
          </h2>
        </div>

        <div className="flex flex-col gap-4 px-6 py-5">
          <ul className="flex flex-col gap-2 font-sans text-sm text-ink-2">
            <li>
              <strong className="text-ink">{workable.toLocaleString('pt-BR')}</strong> cliente(s)
              entram na fila de trabalho agora
              {membersBlocked > 0 && (
                <>
                  {' '}
                  ({membersBlocked.toLocaleString('pt-BR')} ficam registrados como bloqueados, fora
                  da fila)
                </>
              )}
              .
            </li>
            <li>
              Distribuição: <strong className="text-ink">{strategyLabel}</strong>
              {vendorPoolSize > 0 ? (
                <>
                  {' '}
                  — até <strong className="text-ink">{vendorPoolSize}</strong> vendedor(es)
                  habilitado(s) podem atender.
                </>
              ) : (
                <> — nenhum vendedor habilitado identificado ainda.</>
              )}
            </li>
            <li>A campanha passa a aparecer na operação assim que ativada — a ação é auditada.</li>
          </ul>

          {submitError && (
            <div
              role="alert"
              className="rounded-sm px-4 py-3 border-l-[3px]"
              style={{ borderColor: 'var(--danger)', background: 'var(--danger-bg)' }}
            >
              <p className="font-sans font-semibold text-sm text-danger">Não foi possível ativar</p>
              <p className="font-sans text-xs text-ink-2 mt-0.5">{submitError}</p>
            </div>
          )}

          <div
            className="flex flex-col gap-2.5 pt-1 mt-1"
            style={{ borderTop: '1px solid var(--border)' }}
          >
            <Button
              type="button"
              variant="secondary"
              disabled={isPending}
              className={cn('w-full justify-center')}
              onClick={() => void handleConfirm()}
            >
              {isPending ? 'Ativando…' : submitError ? 'Tentar novamente' : 'Confirmar ativação'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              disabled={isPending}
              className="w-full justify-center"
            >
              Cancelar
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
